import { Link, Navigate, useLocation, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import '../styles/list.css';

/**
 * Old request URLs → the `/tasks/*` routes. They are in Slack threads, emails and
 * runbooks, so each keeps working (App.tsx mounts them; query strings survive):
 *
 *  - `/requests/*`      → `/tasks/*` (the previous name; same ids)
 *  - `/access/:id`      → `/tasks/:id` (access requests kept their numbers)
 *  - `/access/new?type=&entityId=&name=` → `/tasks/new?template=access&type=&entityId=&name=`
 *  - `/changes/new`     → `/tasks/new?template=change`
 *  - `/changes/:id`     → looked up through `taskByLegacyCr` (change requests were renumbered)
 *  - `/access`, `/changes` → `/tasks`
 */
export function NewTaskRedirect({ template }: { template: 'access' | 'change' }) {
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  q.delete('template');
  const rest = q.toString();
  return <Navigate replace to={`/tasks/new?template=${template}${rest ? `&${rest}` : ''}`} />;
}

const BY_LEGACY_CR = gql`
  query TaskByLegacyCr($id: Int!) {
    taskByLegacyCr(id: $id) {
      id
    }
  }
`;

export function LegacyChangeRedirect() {
  const { id = '' } = useParams();
  const n = /^\d{1,15}$/.test(id) ? Number(id) : NaN;
  const { data, error } = useQuery<{ taskByLegacyCr: { id: number } }>(BY_LEGACY_CR, {
    variables: { id: n },
    skip: Number.isNaN(n),
    fetchPolicy: 'network-only',
  });
  if (data?.taskByLegacyCr) return <Navigate replace to={`/tasks/${data.taskByLegacyCr.id}`} />;
  if (error || Number.isNaN(n)) {
    return (
      <div className="page" data-testid="legacy-cr-notfound">
        <div className="empty-state">
          <div className="empty-title">Change request CR-{id} was not found</div>
          <div className="empty-body">
            It may not exist, or your role cannot see it. Change requests are now tasks; search for{' '}
            <code>CR-{id}</code> on the tasks list.
          </div>
          <div className="empty-actions btn-row">
            <Link to={`/tasks?q=${encodeURIComponent(`CR-${id}`)}`} className="btn btn-sm">
              Search tasks
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <p className="rql-redirect muted" data-testid="legacy-cr-resolving">
      Opening CR-{id}…
    </p>
  );
}
